/* eslint-disable @typescript-eslint/no-explicit-any,react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { DMAFileTypes } from 'interfaces/ia.interface.ts';
import { bytesToSize, getProperKeyNameDummySearchString } from 'utils/index.ts';
import { fileLinkURIs, listFilesRequest, resolveFirstFileLink } from 'utils/FileUploadAPI.ts';
import { Box, useTheme } from '@mui/material';
import useStyles from './Documents.styles';
import { ToastMessage, Typo } from 'components/primitive';
import { ToastStatus } from '@/common/enum';
import { DropFileZone, FileContainer } from 'components/fragment';
import useUploadForm from 'hooks/useUploadForm.ts';

type DocumentsProps = {
  readOnly: boolean;
  uuid: string;
  authToken: string;
  pvDataID: number | string;
  hasOldData?: boolean;
  files?: DMAFileTypes[];
  title?: string;
  defaultHeaderTitle?: string;
  defaultReadOnlyTitleTitle?: string;
  filesQueuedCallback: React.Dispatch<React.SetStateAction<any>>;
  filesDeletedCallback: React.Dispatch<React.SetStateAction<any>>;
  filesUploadedCallback?: React.Dispatch<React.SetStateAction<any>>;
};

const Documents: React.FC<DocumentsProps> = ({
  readOnly,
  hasOldData = false,
  title = 'Documents',
  defaultHeaderTitle = 'File Upload',
  defaultReadOnlyTitleTitle = 'Please upload a document.',
  filesQueuedCallback,
  filesDeletedCallback,
  filesUploadedCallback,
  ...props
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { downloadForm, uploadedDocuments, inProgress } = useUploadForm<DMAFileTypes>(
    Boolean(props.files && props.files.length > 0)
  );
  const [listError, setListError] = useState(false);
  useEffect(() => {
    if (filesUploadedCallback) {
      filesUploadedCallback(uploadedDocuments);
    }
  }, [uploadedDocuments]);

  useEffect(() => {
    const fetchData = async () => {
      const { url, config } = listFilesRequest(props.uuid, props.authToken);
      await downloadForm(url, config, props.files || []);
    };

    if (props.uuid && props.authToken) {
      setListError(false);
      fetchData().catch((err) => {
        console.error('🚀 Documents.tsx fetchData err: ', err);
        setListError(true);
      });
    }
  }, [props.uuid, props.authToken, hasOldData, props.files]);

  // *: Resolves to the file's link when the browser blocked the new tab, so the row
  // *: can offer it inline; null once the file opened or the download failed.
  // *: happy: link resolves -> the pre-opened tab navigates to it.
  // *: nil/null: no stored path, or no token -> resolveFirstFileLink rejects -> toast.
  // *: empty/zero: blocked tab (null or closed handle) -> the link is returned.
  // *: upstream error: non-2xx / timeout / no https url -> tab closed, toast.
  const downloadFile = async (item: DMAFileTypes, preferredURI?: string) => {
    const uris = fileLinkURIs(item, preferredURI);
    // *: Opened before the link request, while the click still counts as a user
    // *: gesture: Safari blocks window.open once an await has passed. No
    // *: `noopener` feature, which would always return a null handle.
    const tab = uris.length ? window.open('', '_blank') : null;
    try {
      const url = await resolveFirstFileLink(uris, props.authToken);
      if (!tab || tab.closed) return url;
      // *: Detached before it loads the file's origin, which gets no handle on this page.
      tab.opener = null;
      tab.location.href = url;
      return null;
    } catch (err) {
      tab?.close();
      console.error('🚀 Documents.tsx downloadFile err: ', err);
      toast.error(
        <ToastMessage
          status={ToastStatus.ERROR}
          message='Unable to open this file. Please try again.'
        />
      );
      return null;
    }
  };

  return (
    <Box sx={classes.container} hidden={inProgress}>
      <Typo sx={classes.title}>
        {uploadedDocuments.length > 0
          ? title
          : readOnly
            ? defaultReadOnlyTitleTitle
            : defaultHeaderTitle}
      </Typo>
      {readOnly ? (
        <Box sx={classes.content}>
          {listError ? (
            <Box sx={classes.error}>
              <Typo role='alert' sx={classes.errorText}>
                Unable to load documents
              </Typo>
            </Box>
          ) : !inProgress && Array.isArray(uploadedDocuments) && uploadedDocuments.length > 0 ? (
            uploadedDocuments.map((file, index) => {
              if (!file?.pvVoid || file.deleted === 0 || file.deleted === null) {
                const fileName = Object.prototype.hasOwnProperty.call(file, 'cbrnDataFileName')
                  ? file.cbrnDataFileName
                  : Object.prototype.hasOwnProperty.call(file, 'file_name')
                    ? file.file_name
                    : '';
                const hasDummyName: boolean = fileName.indexOf('_DUMMYSEARCHSTRING_') >= 0;
                const name = hasDummyName ? getProperKeyNameDummySearchString(fileName) : fileName;
                const byteSize = Object.prototype.hasOwnProperty.call(file, 'cbrnDataFileSize')
                  ? bytesToSize(file?.cbrnDataFileSize ?? 0)
                  : bytesToSize(file?.file_size ?? 0);
                return (
                  <FileContainer
                    key={file?.pvDataID || index}
                    authToken={props.authToken || ''}
                    readOnly={true}
                    item={file}
                    name={name}
                    bytes={byteSize}
                    downloadFile={downloadFile}
                  />
                );
              }
              return <></>;
            })
          ) : (
            <Box sx={classes.empty}>
              <Typo sx={classes.emptyText}>No document added</Typo>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={classes.content}>
          <DropFileZone
            readOnly={readOnly}
            uuid={props.uuid}
            authToken={props.authToken}
            hasOldData={hasOldData}
            files={props.files}
            filesQueuedCallback={filesQueuedCallback}
            filesDeletedCallback={filesDeletedCallback}
          />
        </Box>
      )}
    </Box>
  );
};

export default Documents;
